import React, { useState, useEffect } from "react"
import { Box, Typography, Paper, Button, TextField, Grid, Alert, CircularProgress } from "@mui/material"
import { MyLocation } from "@mui/icons-material"

/**
 * Componente MapaUbicacion - Permite ingresar y visualizar coordenadas en un mapa
 *
 * @component
 * @param {Object} props
 * @param {Object} props.value - Objeto con las coordenadas { lat, lng, precision }
 * @param {Function} props.onChange - Función que se ejecuta cuando cambian las coordenadas
 * @param {boolean} props.isMobile - Si es true, el mapa se ve más pequeño (útil para celulares)
 * @param {boolean} props.soloLectura - Si es true, deshabilita la edición (solo vista)
 * @param {string} props.modo - Controla cómo puede ingresar la ubicación el usuario
 *                                "manual": solo puede escribir coordenadas
 *                                "gps": solo puede usar el botón de GPS
 *                                "ambos": puede usar ambas opciones
 * @param {string} props.label - Texto personalizado para el título del componente
 *                               Por defecto: "Ubicación"
 */
const MapaUbicacion = ({
  label = "Ubicación",
  // value: objeto con la estructura { lat: "", lng: "", precision: 0 }
  // Este objeto viene del componente padre y se actualiza cuando cambia
  value = { lat: "", lng: "", precision: 0 },

  // onChange: función que se llama CADA VEZ que cambian las coordenadas
  // Recibe el nuevo objeto { lat, lng, precision }
  onChange,

  // isMobile: adapta el tamaño del mapa para dispositivos móviles
  isMobile,

  // soloLectura: cuando es true, los inputs se deshabilitan (solo para ver)
  soloLectura = false,

  // modo: controla qué métodos de ingreso están disponibles
  // "manual" → solo inputs de texto
  // "gps" → solo botón de ubicación actual
  // "ambos" → muestra ambas opciones
  modo = "ambos",
}) => {
  // Estado interno para manejar las coordenadas mientras el usuario escribe
  const [coordenadas, setCoordenadas] = useState(value)

  // Estado para mostrar el círculo de carga mientras obtenemos la ubicación GPS
  const [buscando, setBuscando] = useState(false)

  // useEffect: sincroniza el estado interno cuando el valor externo (value) cambia
  // Por ejemplo: si el padre carga una ubicación guardada desde una base de datos
  useEffect(() => {
    setCoordenadas(value)
  }, [value])

  /**
   * actualizarCoordenadas - se llama cuando el usuario escribe en los inputs
   * @param {string} campo - "lat" o "lng"
   * @param {string} valor - lo que el usuario escribe
   *
   * Cuando el usuario escribe manualmente, la precisión se pone en 0
   * porque se considera que es una coordenada exacta (el usuario sabe dónde está su predio)
   */
  const actualizarCoordenadas = (campo, valor) => {
    const nuevasCoordenadas = {
      ...coordenadas,
      [campo]: valor,
      precision: 0, // Manual = exacto (el usuario declara que ahí es)
    }
    setCoordenadas(nuevasCoordenadas)
    if (onChange) onChange(nuevasCoordenadas)
  }

  /**
   * obtenerUbicacionActual - usa el GPS del dispositivo
   *
   * IMPORTANTE: La precisión viene DIRECTAMENTE del GPS
   * - Si el dispositivo tiene GPS preciso: puede dar 3-5 metros
   * - Si usa WiFi o torres: puede dar 50-100 metros
   * - Esto es lo REAL, no lo inventamos nosotros
   */
  const obtenerUbicacionActual = () => {
    // Verifica si el navegador soporta geolocalización
    if (!navigator.geolocation) {
      alert("Tu navegador no soporta geolocalización")
      return
    }

    setBuscando(true)

    // Pide la ubicación al dispositivo
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // position.coords.accuracy CONTIENE LA PRECISIÓN REAL EN METROS
        const nuevasCoordenadas = {
          lat: position.coords.latitude, // Latitud
          lng: position.coords.longitude, // Longitud
          precision: position.coords.accuracy, // ← PRECISIÓN REAL DEL GPS
        }
        setCoordenadas(nuevasCoordenadas)
        setBuscando(false)
        if (onChange) onChange(nuevasCoordenadas)
      },
      (error) => {
        setBuscando(false)

        // Mostrar un mensaje más específico según el tipo de error
        let mensaje = "Error al obtener ubicación"

        switch (error.code) {
          case error.PERMISSION_DENIED:
            mensaje = "Permiso denegado para obtener la ubicación"
            break
          case error.POSITION_UNAVAILABLE:
            mensaje = "Información de ubicación no disponible"
            break
          case error.TIMEOUT:
            mensaje = "Tiempo de espera agotado"
            break
        }

        alert(mensaje)
        console.error("Error de geolocalización:", error)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    )
  }

  /**
   * mapUrl - genera la URL de Google Maps para incrustar el mapa
   * Si no hay coordenadas, devuelve string vacío (no muestra mapa)
   */
  const mapUrl =
    coordenadas.lat && coordenadas.lng
      ? `https://maps.google.com/maps?q=${coordenadas.lat},${coordenadas.lng}&z=18&output=embed`
      : ""

  /**
   * mostrarPrecision - genera el texto que se muestra sobre la precisión
   * @returns {string} texto descriptivo de la precisión
   *
   * Casos:
   * - Si precision > 0: viene del GPS (muestra los metros)
   * - Si precision = 0: es ingreso manual (coordenada exacta)
   */
  const mostrarPrecision = () => {
    if (!coordenadas.lat || !coordenadas.lng) return null

    if (coordenadas.precision > 0) {
      return `Precisión GPS: ±${Math.round(coordenadas.precision)}m`
    } else {
      return "Precisión GPS: ±0 (Coordenada exacta)"
    }
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        📍 {label}
      </Typography>

      {/*
        INPUTS MANUALES - solo se muestran si:
        - modo es "manual" O "ambos"
        - Y no está en soloLectura
      */}
      {(modo === "manual" || modo === "ambos") && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Latitud"
              placeholder="Ej: -34.603722"
              value={coordenadas.lat || ""}
              onChange={(e) => actualizarCoordenadas("lat", e.target.value)}
              disabled={soloLectura} // Se deshabilita si soloLectura = true
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Longitud"
              placeholder="Ej: -58.381592"
              value={coordenadas.lng || ""}
              onChange={(e) => actualizarCoordenadas("lng", e.target.value)}
              disabled={soloLectura}
            />
          </Grid>
        </Grid>
      )}

      {/*
        BOTÓN GPS - solo se muestra si:
        - modo es "gps" O "ambos"
        - Y no está en soloLectura
      */}
      {(modo === "gps" || modo === "ambos") && !soloLectura && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<MyLocation />}
            onClick={obtenerUbicacionActual}
            disabled={buscando} // Deshabilita mientras busca ubicación
          >
            {modo === "gps" ? "Obtener ubicación" : "Usar mi ubicación actual"}
          </Button>
        </Box>
      )}

      {/* Indicador de carga mientras se obtiene la ubicación GPS */}
      {buscando && <CircularProgress size={30} sx={{ my: 2 }} />}

      {/*
        MAPA - solo se muestra si hay coordenadas válidas
        Si no hay, muestra un mensaje informativo
      */}
      {coordenadas.lat && coordenadas.lng ? (
        <>
          {/* Muestra la precisión siempre (venga de GPS o manual) */}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {mostrarPrecision()}
          </Typography>

          {/* Contenedor del mapa con altura responsive */}
          <Box
            sx={{
              height: isMobile ? "250px" : "350px", // Más chico en móvil
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <iframe title="Mapa" width="100%" height="100%" src={mapUrl} style={{ border: 0 }} />
          </Box>
        </>
      ) : (
        /* Mensaje según el modo cuando no hay coordenadas */
        <Alert severity="info">
          {modo === "gps"
            ? "Haz clic en 'Obtener ubicación' para cargar tu ubicación actual"
            : "Ingresa las coordenadas"}
        </Alert>
      )}
    </Paper>
  )
}

export default MapaUbicacion
